import type { ICampaignHostPackage, ICampaignHostSubscription } from '../types/global/campaignHostSubscription.types';

const ACTIVE_STATUSES = new Set(['active', 'trialing']);

export function isActiveCampaignHostSubscription(sub: ICampaignHostSubscription | null | undefined): boolean {
    return Boolean(sub && ACTIVE_STATUSES.has(sub.status));
}

export function isLifetimeSubscription(sub: ICampaignHostSubscription | null | undefined): boolean {
    if (!sub) return false;
    return (
        sub.interval === 'lifetime' ||
        sub.stripe_details?.billing_type === 'one_time' ||
        sub.paystack_details?.billing_type === 'one_time'
    );
}

export function formatPackagePrice(pkg: ICampaignHostPackage): string {
    if (pkg.formatted_for_display) return pkg.formatted_for_display;
    const cents = pkg.unit_amount ?? pkg.list_price_cents;
    return formatUsdCents(cents);
}

export function formatUsdCents(cents: number | null | undefined): string {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n / 100);
}

export function formatUsdDollars(amount: number | null | undefined): string {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

export function formatSubscriptionDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    } catch {
        return '—';
    }
}

export function findPackageBySlug(
    packages: ICampaignHostPackage[],
    slug: 'campaign-host-monthly' | 'campaign-host-ltd',
): ICampaignHostPackage | undefined {
    return packages.find(p => p.slug === slug);
}

function slugLooksMonthly(slug: string): boolean {
    const s = slug.toLowerCase();
    return s.includes('monthly') || s.includes('month') || s.includes('pro');
}

function slugLooksLifetime(slug: string): boolean {
    const s = slug.toLowerCase();
    return s.includes('ltd') || s.includes('lifetime') || s.includes('forever') || s.includes('life');
}

/** Monthly tier: known slug, then recurring / slug heuristics. */
export function findMonthlyPackage(packages: ICampaignHostPackage[]): ICampaignHostPackage | undefined {
    const sorted = [...packages].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return (
        findPackageBySlug(sorted, 'campaign-host-monthly') ??
        sorted.find(p => p.billing_type === 'recurring' && p.interval === 'month') ??
        sorted.find(p => p.billing_type === 'recurring') ??
        sorted.find(p => slugLooksMonthly(p.slug) && !slugLooksLifetime(p.slug))
    );
}

/** Lifetime tier: known slug, then lifetime billing / slug heuristics. */
export function findLtdPackage(packages: ICampaignHostPackage[]): ICampaignHostPackage | undefined {
    const sorted = [...packages].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return (
        findPackageBySlug(sorted, 'campaign-host-ltd') ??
        sorted.find(p => p.billing_type === 'lifetime') ??
        sorted.find(p => slugLooksLifetime(p.slug))
    );
}

/**
 * Resolve the two tier cards. Falls back to sort_order when slug/billing_type labels differ in API seed data.
 */
export function resolveTierPackages(packages: ICampaignHostPackage[]): {
    monthly: ICampaignHostPackage | undefined;
    ltd: ICampaignHostPackage | undefined;
} {
    const sorted = [...packages].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    if (!sorted.length) {
        return { monthly: undefined, ltd: undefined };
    }

    let monthly = findMonthlyPackage(sorted);
    let ltd = findLtdPackage(sorted);

    if (monthly && ltd && monthly._id === ltd._id) {
        ltd = sorted.find(p => p._id !== monthly!._id && (p.billing_type === 'lifetime' || slugLooksLifetime(p.slug)));
    }

    if (!monthly && !ltd && sorted.length === 1) {
        return { monthly: sorted[0], ltd: undefined };
    }

    if (!monthly && !ltd && sorted.length >= 2) {
        const lifetimeIdx = sorted.findIndex(p => p.billing_type === 'lifetime' || slugLooksLifetime(p.slug));
        if (lifetimeIdx >= 0) {
            ltd = sorted[lifetimeIdx];
            monthly = sorted.find((_, i) => i !== lifetimeIdx) ?? sorted[0];
        } else {
            monthly = sorted[0];
            ltd = sorted[1];
        }
    }

    if (!monthly && ltd && sorted.length >= 2) {
        const ltdPkg = ltd;
        monthly = sorted.find(p => p._id !== ltdPkg._id);
    }
    if (!ltd && monthly && sorted.length >= 2) {
        const monthlyPkg = monthly;
        ltd = sorted.find(p => p._id !== monthlyPkg._id);
    }

    return { monthly, ltd };
}

/** Display price for tier card; optional LTD promo overrides list/stripe display. */
export function formatTierPackagePrice(
    pkg: ICampaignHostPackage,
    options?: { ltdPromoActive?: boolean; promoPriceCents?: number | null },
): string {
    const isLtd = pkg.billing_type === 'lifetime' || slugLooksLifetime(pkg.slug);
    if (isLtd && options?.ltdPromoActive && options.promoPriceCents != null) {
        return formatUsdCents(options.promoPriceCents);
    }
    return formatPackagePrice(pkg);
}

export function packagePriceSuffix(pkg: ICampaignHostPackage): string {
    if (pkg.billing_type === 'lifetime' || pkg.interval === null) return 'One-time';
    if (pkg.price_interval) return pkg.price_interval;
    if (pkg.interval === 'month') return '/mo';
    if (pkg.interval === 'year') return '/yr';
    return '';
}

export function formatSubscriptionDisplayPrice(sub: ICampaignHostSubscription): string {
    const formatted = sub.stripe_details?.price_formatted ?? sub.paystack_details?.price_formatted;
    if (formatted) return formatted;
    const cents =
        sub.stripe_details?.price_amount ?? sub.paystack_details?.next_invoice_amount ?? sub.package?.list_price_cents;
    const currency = sub.stripe_details?.price_currency ?? sub.paystack_details?.currency ?? sub.package?.currency;
    if (currency && currency.toUpperCase() !== 'USD') {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(Number(cents ?? 0) / 100);
    }
    return formatUsdCents(cents);
}

export function subscriptionPriceIntervalLabel(sub: ICampaignHostSubscription): string {
    if (isLifetimeSubscription(sub)) return 'ONE-TIME PAYMENT';
    const interval = sub.stripe_details?.price_interval ?? sub.paystack_details?.price_interval;
    if (interval) return interval.toUpperCase();
    if (sub.interval === 'month') return '/MO';
    if (sub.interval === 'year') return '/YR';
    return '/MO';
}

export function formatBillingHistoryAmount(item: { amount: number; currency: string }): string {
    const currency = (item.currency || 'usd').toUpperCase();
    if (currency === 'USD') return formatUsdCents(item.amount);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: item.currency || 'USD',
    }).format(item.amount / 100);
}
